import { animate, style, transition, trigger } from '@angular/animations';
import { NgClass, UpperCasePipe } from '@angular/common';
import { HttpClient, httpResource, HttpResourceRef } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { form, FormField } from '@angular/forms/signals';
import { NavigationEnd, Params, Router } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faArrowRightFromBracket,
  faCalendar,
  faChevronDown,
  faChevronUp,
  faCircleArrowLeft,
  faGrip,
  faHouse,
  faLayerGroup,
  faLifeRing,
  faMagnifyingGlass,
  faPenToSquare,
  faThumbtack,
  faUser,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { filter } from 'rxjs';
import packageJson from '../../../../../package.json';
import { environment } from '../../../../environments/environment';
import { InputFileComponent } from '../../../shared/components/input/input-file/input-file.component';
import { NumberOnlyDirective } from '../../../shared/directives/numberOnly.directive';
import { TabNav } from '../../../shared/models/component/tab-nav.model';
import { FirstLetterCapitalizePipe } from '../../../shared/pipes/firstLetterCapitalize.pipe';
import { ModalService } from '../../../shared/services/modal/modal.service';
import { NavbarParamService } from '../../../shared/services/navbarParam/navbarParam.service';
import { matchValidator } from '../../../shared/validators/match.validators';
import { passwordSecureValidator } from '../../../shared/validators/passwordSecure.validators';
import { AccessAdminGate } from '../../models/access-admin-gate.model';
import { AccessApp } from '../../models/accessApp.model';
import NavbarItem from '../../models/navbar-item.model';
import { EditProfile, UserData } from '../../models/userData.model';
import { NavbarStateService } from '../../services/navbar-state/navbar-state.service';
import { userStore } from '../../user.store';

type app = {
  name: string;
  link: string;
};
@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  animations: [
    trigger('toggleAppList', [
      transition(':enter', [style({ transform: 'translateX(-300px)' }), animate('200ms ease-in-out', style({ transform: 'translateX(0)' }))]),
      transition(':leave', [style({ transform: 'translateX(0)' }), animate('200ms ease-in-out', style({ transform: 'translateX(-300px)' }))]),
    ]),
    trigger('toggleCurrentNav', [
      transition(':enter', [style({ transform: 'translateX(300px)' }), animate('200ms ease-in-out', style({ transform: 'translateX(0)' }))]),
      transition(':leave', [style({ transform: 'translateX(0)' }), animate('200ms ease-in-out', style({ transform: 'translateX(300px)' }))]),
    ]),
  ],
  standalone: true,
  imports: [
    FaIconComponent,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    InputFileComponent,
    ImageCropperComponent,
    UpperCasePipe,
    FirstLetterCapitalizePipe,
    NumberOnlyDirective,
    FormField,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class NavbarComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private readonly modalService = inject(ModalService);
  private formBuilder = inject(FormBuilder);
  private http = inject(HttpClient);
  private navbarParamService = inject(NavbarParamService);
  readonly navbarState = inject(NavbarStateService);
  wsStatusRes = httpResource(() => `${environment.socketApiCallUrl}/ping`);
  apiStatusRes = httpResource(() => `${environment.apiUrl}/common/ping`);
  timeout: any;
  readonly store = inject(userStore);

  searchForm = form(
    signal({
      search: '',
    }),
  );
  navListItem: NavbarItem[] = [];

  angleLeft = faAngleLeft;
  angleRight = faAngleRight;

  get _appName(): app {
    return this.navbarState.appName();
  }

  get _toggleAppList(): boolean {
    return this.navbarState.toggleAppList();
  }

  get getNavCollapse(): boolean {
    return this.navbarState.navCollapse();
  }
  filteredApp = computed(() => {
    const search = this.searchForm.search().value();
    if (search != '') return this.store.access_app().filter((app) => app.name_application.toLowerCase().includes(search.toLowerCase()));
    return this.store.access_app();
  });
  lifeRing = faLifeRing;
  penToSquare = faPenToSquare;
  backArrow = faCircleArrowLeft;
  user = faUser;
  arrowRightFromBracket = faArrowRightFromBracket;
  layerGroup = faLayerGroup;
  allowedFileTypes = ['image/png', 'image/jpeg', 'image/jpg'];
  version = packageJson.version;
  lockSubmit: boolean = false;
  setCrop: boolean = false;
  showPreview: boolean = false;

  @HostListener('document:click', ['$event'])
  clickout(event: Event) {
    if (
      this.toggleProfile &&
      !(event.target as HTMLHtmlElement).classList.contains('profil--header') &&
      !(event.target as HTMLHtmlElement).classList.contains('profil--header--children')
    ) {
      this.toggleProfile = false;
    }
    if (this.mobileProfileOpen) {
      this.mobileProfileOpen = false;
    }
  }

  toggleBugModal: WritableSignal<boolean> = signal<boolean>(false);
  toggleNav: boolean = false;
  toggleProfile: boolean = false;
  toggleEditProfil: boolean = false;
  croppedImage: string = '';
  profilForm: FormGroup<{
    firstname: FormControl<string>;
    lastname: FormControl<string>;
    mail: FormControl<string>;
    phone: FormControl<string | null>;
    mobile_phone: FormControl<string | null>;
    old_password: FormControl<string>;
    new_password: FormControl<string | null>;
    confirm_new_password: FormControl<string | null>;
  }> = new FormGroup({
    firstname: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    lastname: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    mail: new FormControl('', { validators: [Validators.required, Validators.email], nonNullable: true }),
    phone: new FormControl('', [Validators.pattern('^[0-9]*$'), Validators.minLength(10), Validators.maxLength(10)]),
    mobile_phone: new FormControl('', [Validators.pattern('^[0-9]*$'), Validators.minLength(10), Validators.maxLength(10)]),
    old_password: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    new_password: new FormControl(''),
    confirm_new_password: new FormControl(''),
  });
  reader = new FileReader();
  photo_users!: string;
  old_photo_users!: string;
  bugForm!: FormGroup;
  name_app_for_tracking!: string;
  private apiFailCount = 0;
  private wsFailCount = 0;

  private readonly MAX_FAIL_COUNT = 3;
  private readonly COOLDOWN_MS = 5 * 60 * 1000;
  private readonly destroyRef = inject(DestroyRef);
  faCalendar = faCalendar;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faHouse = faHouse;
  faGrip = faGrip;
  faMagnifyingGlass = faMagnifyingGlass;
  faXmark = faXmark;
  faThumbtack = faThumbtack;

  private readonly RECENT_APPS_KEY = 'navbar_recent_apps';
  private readonly NAVIGATION_MODE_KEY = 'navbar_navigation_mode';
  private readonly MAX_RECENT_APPS = 5;
  showAllApps: boolean = false;
  listHasScroll: boolean = false;
  maxCollapsedApps: number = 5;
  private listResizeObserver?: ResizeObserver;
  @ViewChild('appListEl') appListEl?: ElementRef<HTMLDivElement>;
  recentApps: WritableSignal<app[]> = signal<app[]>([]);
  mobileDrawerOpen: WritableSignal<boolean> = signal<boolean>(false);
  mobileProfileOpen: boolean = false;
  searchActive: boolean = false;
  desktopLauncherOpen: boolean = false;
  useNewDesktopMenu: boolean = false;

  // ── Drawer latéral desktop ──────────────────────────────────────────────
  sideDrawerOpen: boolean = false;
  sideDrawerTitle: string = '';

  @HostListener('document:keydown', ['$event']) onKeydownHandler(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Escape') {
      if (this.toggleEditProfil) this.toggleEditProfil = false;
      if (this.sideDrawerOpen) this.closeSideDrawer();
      if (this.desktopLauncherOpen) this.closeDesktopLauncher();
    }
    if (this.useNewDesktopMenu && (keyboardEvent.ctrlKey || keyboardEvent.metaKey) && keyboardEvent.key.toLowerCase() === 'k') {
      keyboardEvent.preventDefault();
      this.toggleDesktopLauncher();
    }
  }
  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.navListItem = [];
        this.toggleNav = false;
        this.toggleProfile = false;
        this.mobileDrawerOpen.set(false);
        this.mobileProfileOpen = false;
        this.searchActive = false;
        this.sideDrawerOpen = false;
        this.desktopLauncherOpen = false;
      });
    effect(() => {
      if (this.wsStatusRes.error()) {
        this.wsFailCount++;
        console.error('WebSocket connection error:', this.wsStatusRes.error()?.message.replaceAll('https://', '').replaceAll('http://', ''));
        const content = `WebSocket server ${environment.socketApiCallUrl.replaceAll('https://', '').replaceAll('http://', '')} down !!! Error: ${this.wsStatusRes.error()?.message.replaceAll('https://', '').replaceAll('http://', '')}`;
        if (environment.enableDiscordAlert && this.wsFailCount >= this.MAX_FAIL_COUNT && this.shouldNotify('ws')) {
          this.sendDiscord(content);
        }
      }
      if (this.apiStatusRes.error()) {
        this.apiFailCount++;
        console.error('API connection error:', this.apiStatusRes.error()?.message.replaceAll('https://', '').replaceAll('http://', ''));
        const content = `API server ${environment.apiUrl.replaceAll('https://', '').replaceAll('http://', '')} down !!! Error: ${this.apiStatusRes.error()?.message.replaceAll('https://', '').replaceAll('http://', '')}`;
        if (environment.enableDiscordAlert && this.apiFailCount >= this.MAX_FAIL_COUNT && this.shouldNotify('api')) {
          this.sendDiscord(content);
        }
      }
    });
  }

  ngOnInit() {
    this.applyNavigationMode(localStorage.getItem(this.NAVIGATION_MODE_KEY) === 'modern');
    this.timeout = setInterval(() => {
      this.wsStatusRes.reload();
      this.apiStatusRes.reload();
    }, 60000);
    try {
      const stored = localStorage.getItem(this.RECENT_APPS_KEY);
      if (stored) this.recentApps.set(JSON.parse(stored) as app[]);
    } catch {}
    // Calcul initial après rendu
    setTimeout(() => {
      this.refreshListScroll();
      this.calculateMaxCollapsedApps();
      this.observeListResize();
    }, 80);
  }

  ngOnDestroy() {
    clearInterval(this.timeout);
    this.listResizeObserver?.disconnect();
    document.body.classList.remove('new-navigation');
  }

  setNavigationMode(useNewMenu: boolean): void {
    this.applyNavigationMode(useNewMenu);
    localStorage.setItem(this.NAVIGATION_MODE_KEY, useNewMenu ? 'modern' : 'legacy');
  }

  private applyNavigationMode(useNewMenu: boolean): void {
    this.useNewDesktopMenu = useNewMenu;
    document.body.classList.toggle('new-navigation', useNewMenu);
    this.toggleProfile = false;
    this.closeDesktopLauncher();
    this.closeSideDrawer();
    this.navbarState.toggleAppList.set(true);
  }

  logout(): void {
    sessionStorage.clear();
    localStorage.clear();
    this.modalService.openMiniHeadband('', '', '', () => {
      this.modalService.closeMiniHeadband();
    });
    this.modalService.closeMiniHeadband();
    this.store.logout();
  }

  toggleAppListFn() {
    this.navListItem = [];
    this.navbarState.toggleAppList.set(!this.navbarState.toggleAppList());
    this.toggleProfile = false;
  }

  toggleProfileFn() {
    this.toggleProfile = !this.toggleProfile;
  }

  toggleDesktopProfile(event: MouseEvent): void {
    event.stopPropagation();
    this.toggleProfileFn();
  }

  isAppActive(link: string) {
    const appName = this.normalizeAppIdentifier(this.router.url.split('/')[1] ?? '');
    const normalizedLink = this.normalizeAppIdentifier(link);

    if (normalizedLink.includes('portail')) {
      return appName.includes('portail');
    }
    if (normalizedLink.includes('carriere')) {
      return appName.includes('carriere');
    }
    if (normalizedLink.includes('objectifsprime')) {
      return appName.includes('objectifs');
    }

    if (normalizedLink.includes('creance')) {
      return appName.includes('suivicreance');
    }
    return appName.includes(normalizedLink);
  }

  private normalizeAppIdentifier(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toLowerCase();
  }

  isAppMenuItemActive(link: string): boolean {
    const currentPath = this.router.url.split('?')[0]; // retire les queryParams
    return currentPath === link;
  }

  openAppNav(app: app) {
    this.name_app_for_tracking = app.name;
    this.navbarState.appName.set({ name: app.name, link: app.link ? app.link : '/' });
    if (app.name === 'Accueil') {
      this.navListItem = this.getPortailNav();
    } else {
      this.navListItem = this.store.getMenuOfApp(app.name);
    }
    // Sur desktop (≥1280px) : drawer latéral, la liste d'apps reste visible
    if (window.innerWidth >= 1280) {
      this.desktopLauncherOpen = false;
      this.sideDrawerTitle = app.name;
      this.sideDrawerOpen = true;
      // Ne plus swapper la vue principale
      this.navbarState.toggleAppList.set(true);
    } else {
      this.navbarState.toggleAppList.set(false);
    }
  }

  closeSideDrawer(): void {
    this.sideDrawerOpen = false;
    this.navListItem = [];
    this.sideDrawerTitle = '';
  }

  toggleDesktopLauncher(): void {
    this.desktopLauncherOpen = !this.desktopLauncherOpen;
    this.toggleProfile = false;
    if (this.desktopLauncherOpen) {
      this.closeSideDrawer();
      setTimeout(() => document.querySelector<HTMLInputElement>('.desktop-launcher__search')?.focus());
    } else {
      this.searchForm.search().value.set('');
    }
  }

  closeDesktopLauncher(): void {
    this.desktopLauncherOpen = false;
    this.searchForm.search().value.set('');
  }

  navigateDesktopHome(): void {
    this.closeDesktopLauncher();
    this.closeSideDrawer();
    this.router.navigate(['/portail']);
  }

  openDesktopHomeMenu(): void {
    this.openAppNav({ name: 'Accueil', link: '/portail' });
  }

  navigateTo(link: string, event: MouseEvent, params?: string) {
    if (this.isAppMenuItemActive(link)) {
      return;
    }
    if (link.includes(':id_user')) {
      link = link.replaceAll(':id_user', this.store.user().id.toString());
    }

    this.addToRecentApps(this._appName);
    this.toggleNav = false;
    this.toggleProfile = false;
    this.navListItem = [];
    this.mobileDrawerOpen.set(false);
    this.searchActive = false;
    if (event.ctrlKey || event.metaKey) {
      this.navbarState.toggleAppList.set(true);
      const url = params
        ? this.router.serializeUrl(this.router.createUrlTree([link], { queryParams: this.convertQueryParamsToObj(params) }))
        : this.router.serializeUrl(this.router.createUrlTree([link]));
      window.open(url, '_blank');
    } else {
      params ? this.router.navigate([link], { queryParams: this.convertQueryParamsToObj(params) }) : this.router.navigate([link]);
    }
  }

  gotToMyProfile() {
    this.toggleNav = false;
    this.toggleProfile = false;
    this.router.navigate(['/portail/utilisateur/' + this.store.user().id]);
  }

  loadFormProfil(): void {
    this.profilForm.patchValue({
      firstname: this.store.user().firstname,
      lastname: this.store.user().lastname,
      mail: this.store.user().mail,
      phone: this.store.user().phone,
      mobile_phone: this.store.user().mobile_phone,
    });
  }

  convertQueryParamsToObj(tabQueryParams: string) {
    const _tabQueryParams = JSON.parse(tabQueryParams) as TabNav.TabQueryParams[];
    return _tabQueryParams.reduce((result: Params, param) => {
      if (param.value[0] != null && param.value[0].includes('baseOn')) {
        const baseOn: string = param.value[0].replaceAll('baseOn:', '');
        switch (baseOn.split('.')[0]) {
          case 'dateService':
            // baseOn:dateService.nomDeMaMethode
            if (baseOn.includes('|')) {
              const dateParams: string[] = baseOn.split('|');
              result[param.cat] =
                `${this.navbarParamService.getDateServiceValue(dateParams[0])}|${this.navbarParamService.getDateServiceValue(dateParams[1])}`;
            } else {
              result[param.cat] = this.navbarParamService.getDateServiceValue(baseOn);
            }
            break;
          case 'httpResource':
            // baseOn:httpResource.nomDeMaRessource
            this.navbarParamService.triggerHttpRessource.set(true);
            const resource = this.navbarParamService[baseOn.split('.')[1] as keyof NavbarParamService] as HttpResourceRef<any>;
            const value = resource.value();
            result[param.cat] = value !== undefined ? value : null;

            break;
        }
      } else {
        result[param.cat] = param.value;
      }
      return result;
    }, {});
  }

  toggleEditProfilFn() {
    this.router.navigate(['/profile'], { queryParams: { onglet: 'informations-generales' } });
  }

  toggleCalendarFn() {
    this.router.navigate(['/calendrier']);
  }

  loadDataUser(): void {
    const userData = this.store.user();
    this.loadFormProfil();
    this.old_photo_users = userData.photo;
    this.profilForm.controls.new_password.valueChanges.subscribe((value) => {
      if (value !== null && value.length > 0) {
        this.profilForm.setValidators([matchValidator('new_password', 'confirm_new_password'), passwordSecureValidator('new_password')]);
      } else {
        this.profilForm.clearValidators();
      }
    });
  }

  onSubmit() {
    if (this.profilForm.invalid) {
      return;
    }
    const profil: EditProfile = {
      phone: this.profilForm.controls.phone.value !== null ? this.profilForm.controls.phone.value : undefined,
      mobile_phone: this.profilForm.controls.mobile_phone.value !== null ? this.profilForm.controls.mobile_phone.value : undefined,
      firstname: this.profilForm.controls.firstname.value,
      lastname: this.profilForm.controls.lastname.value,
      mail: this.profilForm.controls.mail.value,
      old_password: this.profilForm.controls.old_password.value,
      new_password: this.profilForm.controls.new_password.value != null ? this.profilForm.controls.new_password.value : undefined,
      photo: this.photo_users ? this.photo_users : undefined,
    };
    this.toggleEditProfil = false;
    this.store.editProfile(profil);
  }

  onFileChange(event: string[]) {
    this.photo_users = event[0];
    this.setCrop = true;
  }

  onFileRemove(event: number) {
    this.photo_users = '';
    this.showPreview = false;
  }

  onToggleBugModal() {
    this.toggleBugModal.set(!this.toggleBugModal());
    this.bugForm = this.formBuilder.group({
      description: ['', [Validators.required, Validators.maxLength(4096)]],
      subject: ['', [Validators.required]],
    });
  }

  onSubmitBug() {
    const bug = this.bugForm.value.description;
    const subject = this.bugForm.value.subject;
    let firstName = this.store.user().firstname[0].toUpperCase() + this.store.user().firstname.slice(1, this.store.user().firstname.length);
    const mail = this.store.user().mail;
    if (!this.lockSubmit) {
      this.lockSubmit = true;
      this.sendBugReport(
        bug,
        subject,
        this.store.user().photo,
        `${firstName} ${this.store.user().lastname.toUpperCase()}`,
        window.location.href,
        this.version,
        mail,
      ).subscribe((res) => {
        this.toggleBugModal.set(false);
        this.lockSubmit = false;
      });
    }
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64!;
  }

  onCrop() {
    this.photo_users = this.croppedImage;
    this.setCrop = false;
    this.showPreview = true;
  }

  sendBugReport(message: string, subject: string, photo: string, name: string, url: string, version: string, mail: string) {
    const content = {
      description: message,
      subject: subject,
      mail: mail,
      version: version,
      url: url,
      name: name,
      photo_url: photo !== 'null' && photo !== undefined && photo != null ? photo + '&width=128&height=128' : null,
    };
    this.toggleBugModal.set(false);
    return this.http.post('https://support.paddock-one.com/ticket', content);
  }

  getFormattedAppName(appName: string) {
    return appName.toLowerCase().replace(/ /g, '-').toLocaleLowerCase();
  }

  addToRecentApps(recentApp: app): void {
    const current = this.recentApps();
    const filtered = current.filter((a) => a.name !== recentApp.name);
    const updated = [recentApp, ...filtered].slice(0, this.MAX_RECENT_APPS);
    this.recentApps.set(updated);
    this.closeSideDrawer();
    this.closeMobileDrawer();
    try {
      localStorage.setItem(this.RECENT_APPS_KEY, JSON.stringify(updated));
    } catch {}
    // Recalculer après rendu car la section "Récents" a changé de taille
    setTimeout(() => this.calculateMaxCollapsedApps(), 60);
  }

  toggleShowAllAppsFn(): void {
    this.showAllApps = !this.showAllApps;
    if (this.showAllApps) {
      // Scroll automatique jusqu'aux nouvelles apps après rendu
      setTimeout(() => {
        const el = this.appListEl?.nativeElement;
        if (el) {
          el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
          this.updateListScroll(el);
        }
      }, 60);
    } else {
      setTimeout(() => {
        this.appListEl?.nativeElement?.scrollTo({ top: 0, behavior: 'smooth' });
        this.refreshListScroll();
      }, 60);
    }
  }

  onListScroll(event: Event): void {
    this.updateListScroll(event.target as HTMLDivElement);
  }

  private refreshListScroll(): void {
    const el = this.appListEl?.nativeElement;
    if (el) this.updateListScroll(el);
    else this.listHasScroll = false;
  }

  private calculateMaxCollapsedApps(): void {
    const listEl = this.appListEl?.nativeElement;
    if (!listEl) return;

    const listHeight = listEl.clientHeight;
    const sections = listEl.querySelectorAll('.nav-section');
    if (sections.length === 0) return;

    // Hauteur occupée par les sections précédentes (Accès rapide, Récents)
    let otherSectionsHeight = 0;
    for (let i = 0; i < sections.length - 1; i++) {
      otherSectionsHeight += (sections[i] as HTMLElement).offsetHeight;
    }

    // Section "Mes applications"
    const appSection = sections[sections.length - 1];
    const label = appSection.querySelector('.nav-section__label');
    const labelHeight = label ? (label as HTMLElement).offsetHeight : 0;

    // Hauteur d'un item (mesuré depuis n'importe quel item rendu)
    const anyItem = listEl.querySelector('.nav_list--item');
    const itemHeight = anyItem ? (anyItem as HTMLElement).offsetHeight : 36;

    // Espace disponible pour les items d'application
    const availableHeight = listHeight - otherSectionsHeight - labelHeight;
    const max = Math.max(1, Math.floor(availableHeight / itemHeight));

    if (max !== this.maxCollapsedApps) {
      this.maxCollapsedApps = max;
    }
  }

  private observeListResize(): void {
    const listEl = this.appListEl?.nativeElement;
    if (!listEl) return;
    this.listResizeObserver?.disconnect();
    this.listResizeObserver = new ResizeObserver(() => {
      this.calculateMaxCollapsedApps();
      this.refreshListScroll();
    });
    this.listResizeObserver.observe(listEl);
  }

  private updateListScroll(el: HTMLElement): void {
    this.listHasScroll = el.scrollTop + el.clientHeight < el.scrollHeight - 6;
  }

  openMobileApps(): void {
    this.mobileDrawerOpen.set(!this.mobileDrawerOpen());
    this.searchActive = false;
    this.mobileProfileOpen = false;
  }

  closeMobileDrawer(): void {
    this.mobileDrawerOpen.set(false);
  }

  openMobileSearch(): void {
    this.searchActive = !this.searchActive;
    if (this.searchActive) {
      this.mobileDrawerOpen.set(false);
      this.mobileProfileOpen = false;
      this.searchForm.search().value.set('');
    }
  }

  closeMobileSearch(): void {
    this.searchActive = false;
    this.searchForm.search().value.set('');
  }

  navigateHome(): void {
    this.mobileDrawerOpen.set(false);
    this.searchActive = false;
    this.router.navigate(['/portail']);
  }

  toggleMobileProfile(event: Event): void {
    event.stopPropagation();
    this.mobileProfileOpen = !this.mobileProfileOpen;
    if (this.mobileProfileOpen) {
      this.mobileDrawerOpen.set(false);
      this.searchActive = false;
    }
  }

  get hasNotifications(): boolean {
    return this.store.access_app().some((app) => app.notification);
  }

  get visibleApps() {
    const apps = this.store.access_app();
    return this.showAllApps || apps.length <= this.maxCollapsedApps ? apps : apps.slice(0, this.maxCollapsedApps);
  }

  getPortailNav(): NavbarItem[] {
    const accessAdminGate = this.getUserAccessAdminGate(this.store.user());
    switch (this.hasAccessToAdminGate()) {
      case true:
        return [
          {
            label: 'Actualités',
            link: '/portail/actualites',
            icon: 'M13.86 1.989a2.61 2.61 0 0 0-3.726 0L.768 11.527a2.73 2.73 0 0 0 0 3.795l6.684 6.808a2.618 2.618 0 0 0 1.74.664h5.613a2.616 2.616 0 0 0 1.872-.791l6.554-6.675a2.726 2.726 0 0 0 0-3.795l-9.37-9.544zM13.6 19.41l-.935.95a.372.372 0 0 1-.268.114h-.8a.376.376 0 0 1-.247-.096l-.954-.97a.39.39 0 0 1 0-.542l1.337-1.36a.37.37 0 0 1 .53 0l1.338 1.36a.389.389 0 0 1 0 .544zm0-5.711l-3.737 3.808a.374.374 0 0 1-.268.11h-.8a.376.376 0 0 1-.25-.092l-.95-.97a.391.391 0 0 1 0-.544l4.139-4.214a.372.372 0 0 1 .53 0l1.338 1.362a.386.386 0 0 1 0 .54zm0-5.707l-6.54 6.66a.372.372 0 0 1-.268.113h-.8a.373.373 0 0 1-.25-.094L4.79 13.7a.388.388 0 0 1 0-.54l6.943-7.07a.372.372 0 0 1 .53 0l1.338 1.36a.389.389 0 0 1 0 .543z',
            ratio: '0 0 24 24',
          },
          {
            label: 'Trombinoscope',
            link: '/portail/utilisateur',
            icon: 'M12 5.5A3.5 3.5 0 0 1 15.5 9a3.5 3.5 0 0 1-3.5 3.5A3.5 3.5 0 0 1 8.5 9A3.5 3.5 0 0 1 12 5.5M5 8c.56 0 1.08.15 1.53.42c-.15 1.43.27 2.85 1.13 3.96C7.16 13.34 6.16 14 5 14a3 3 0 0 1-3-3a3 3 0 0 1 3-3m14 0a3 3 0 0 1 3 3a3 3 0 0 1-3 3c-1.16 0-2.16-.66-2.66-1.62a5.536 5.536 0 0 0 1.13-3.96c.45-.27.97-.42 1.53-.42M5.5 18.25c0-2.07 2.91-3.75 6.5-3.75s6.5 1.68 6.5 3.75V20h-13v-1.75M0 20v-1.5c0-1.39 1.89-2.56 4.45-2.9c-.59.68-.95 1.62-.95 2.65V20H0m24 0h-3.5v-1.75c0-1.03-.36-1.97-.95-2.65c2.56.34 4.45 1.51 4.45 2.9V20Z',
            ratio: '0 0 24 24',
          },
          {
            label: 'Numéros utiles',
            link: '/portail/annuaire',
            icon: 'M2 21q-.825 0-1.412-.587Q0 19.825 0 19V5q0-.825.588-1.413Q1.175 3 2 3h20q.825 0 1.413.587Q24 4.175 24 5v14q0 .825-.587 1.413Q22.825 21 22 21Zm7-7q1.25 0 2.125-.875T12 11q0-1.25-.875-2.125T9 8q-1.25 0-2.125.875T6 11q0 1.25.875 2.125T9 14Zm10 4l2-2l-1.5-2h-1.65q-.15-.45-.25-.963q-.1-.512-.1-1.037q0-.525.1-1.012q.1-.488.25-.988h1.65L21 8l-2-2q-1.35 1.05-2.175 2.662Q16 10.275 16 12t.825 3.337Q17.65 16.95 19 18ZM2.1 19h13.8q-1.05-1.875-2.9-2.938Q11.15 15 9 15t-4 1.062Q3.15 17.125 2.1 19Z',
            ratio: '0 0 24 24',
          },
          {
            label: 'Administration',
            link: this.getPathByKey(this.getFirstAccess(accessAdminGate)),
            icon: 'M17 17q.625 0 1.062-.438q.438-.437.438-1.062t-.438-1.062Q17.625 14 17 14t-1.062.438q-.438.437-.438 1.062t.438 1.062Q16.375 17 17 17Zm0 3q.775 0 1.425-.363q.65-.362 1.05-.962q-.55-.325-1.175-.5T17 18q-.675 0-1.3.175t-1.175.5q.4.6 1.05.962Q16.225 20 17 20Zm0 2q-2.075 0-3.537-1.462Q12 19.075 12 17q0-2.075 1.463-3.538Q14.925 12 17 12t3.538 1.462Q22 14.925 22 17q0 2.075-1.462 3.538Q19.075 22 17 22Zm-5 0q-3.475-.875-5.737-3.988Q4 14.9 4 11.1V5l8-3l8 3v5.675q-.65-.325-1.462-.5Q17.725 10 17 10q-2.9 0-4.95 2.05Q10 14.1 10 17q0 1.55.588 2.8q.587 1.25 1.487 2.175q-.025 0-.037.013Q12.025 22 12 22Z',
            ratio: '0 0 24 24',
          },
        ];
      default:
        return [
          {
            label: 'Actualités',
            link: '/portail/actualites',
            icon: 'M13.86 1.989a2.61 2.61 0 0 0-3.726 0L.768 11.527a2.73 2.73 0 0 0 0 3.795l6.684 6.808a2.618 2.618 0 0 0 1.74.664h5.613a2.616 2.616 0 0 0 1.872-.791l6.554-6.675a2.726 2.726 0 0 0 0-3.795l-9.37-9.544zM13.6 19.41l-.935.95a.372.372 0 0 1-.268.114h-.8a.376.376 0 0 1-.247-.096l-.954-.97a.39.39 0 0 1 0-.542l1.337-1.36a.37.37 0 0 1 .53 0l1.338 1.36a.389.389 0 0 1 0 .544zm0-5.711l-3.737 3.808a.374.374 0 0 1-.268.11h-.8a.376.376 0 0 1-.25-.092l-.95-.97a.391.391 0 0 1 0-.544l4.139-4.214a.372.372 0 0 1 .53 0l1.338 1.362a.386.386 0 0 1 0 .54zm0-5.707l-6.54 6.66a.372.372 0 0 1-.268.113h-.8a.373.373 0 0 1-.25-.094L4.79 13.7a.388.388 0 0 1 0-.54l6.943-7.07a.372.372 0 0 1 .53 0l1.338 1.36a.389.389 0 0 1 0 .543z',
            ratio: '0 0 24 24',
          },
          {
            label: 'Trombinoscope',
            link: '/portail/utilisateur',
            icon: 'M12 5.5A3.5 3.5 0 0 1 15.5 9a3.5 3.5 0 0 1-3.5 3.5A3.5 3.5 0 0 1 8.5 9A3.5 3.5 0 0 1 12 5.5M5 8c.56 0 1.08.15 1.53.42c-.15 1.43.27 2.85 1.13 3.96C7.16 13.34 6.16 14 5 14a3 3 0 0 1-3-3a3 3 0 0 1 3-3m14 0a3 3 0 0 1 3 3a3 3 0 0 1-3 3c-1.16 0-2.16-.66-2.66-1.62a5.536 5.536 0 0 0 1.13-3.96c.45-.27.97-.42 1.53-.42M5.5 18.25c0-2.07 2.91-3.75 6.5-3.75s6.5 1.68 6.5 3.75V20h-13v-1.75M0 20v-1.5c0-1.39 1.89-2.56 4.45-2.9c-.59.68-.95 1.62-.95 2.65V20H0m24 0h-3.5v-1.75c0-1.03-.36-1.97-.95-2.65c2.56.34 4.45 1.51 4.45 2.9V20Z',
            ratio: '0 0 24 24',
          },
          {
            label: 'Numéros utiles',
            link: '/portail/annuaire',
            icon: 'M2 21q-.825 0-1.412-.587Q0 19.825 0 19V5q0-.825.588-1.413Q1.175 3 2 3h20q.825 0 1.413.587Q24 4.175 24 5v14q0 .825-.587 1.413Q22.825 21 22 21Zm7-7q1.25 0 2.125-.875T12 11q0-1.25-.875-2.125T9 8q-1.25 0-2.125.875T6 11q0 1.25.875 2.125T9 14Zm10 4l2-2l-1.5-2h-1.65q-.15-.45-.25-.963q-.1-.512-.1-1.037q0-.525.1-1.012q.1-.488.25-.988h1.65L21 8l-2-2q-1.35 1.05-2.175 2.662Q16 10.275 16 12t.825 3.337Q17.65 16.95 19 18ZM2.1 19h13.8q-1.05-1.875-2.9-2.938Q11.15 15 9 15t-4 1.062Q3.15 17.125 2.1 19Z',
            ratio: '0 0 24 24',
          },
        ];
    }
  }

  getFirstAccess(accessAdminGate: AccessAdminGate): string | null {
    for (const key of Object.keys(accessAdminGate)) {
      if (accessAdminGate[key as keyof AccessAdminGate] !== 'not-allowed') {
        return key;
      }
    }
    return null;
  }

  addBookmark(id_application: number): void {
    const nbBookMark = this.store.access_app().filter((ni) => ni.is_bookmarked).length;
    if (nbBookMark >= 7) {
      this.modalService.openAlert(
        'error',
        'Limite de favoris atteinte',
        `Vous avez atteint la limite de favoris autorisés (7). Veuillez supprimer un favori existant avant d'en ajouter un nouveau.`,
        () => {
          this.modalService.closeAlert();
        },
      );
      return;
    }
    this.store.addBookmark(id_application);
  }

  deleteBookmark(id: number): void {
    this.store.deleteBookmark(id);
  }

  // ─── Appui long sur une tuile mobile pour (dé)favoriser une application ───
  private static readonly LONG_PRESS_DURATION_MS = 500;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressTriggered = false;
  pressingAppTileId: number | null = null;
  toggledAppTileId: number | null = null;

  onAppTilePressStart(app: AccessApp): void {
    this.longPressTriggered = false;
    this.pressingAppTileId = app.id_application;
    this.longPressTimer = setTimeout(() => {
      this.longPressTriggered = true;
      this.pressingAppTileId = null;
      this.toggleBookmarkFromTile(app);
      navigator.vibrate?.(15);
      this.toggledAppTileId = app.id_application;
      setTimeout(() => (this.toggledAppTileId = null), 400);
    }, NavbarComponent.LONG_PRESS_DURATION_MS);
  }

  onAppTilePressEnd(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    this.pressingAppTileId = null;
  }

  onAppTileClick(app: app, event: Event): void {
    // Un appui long vient de basculer le favori : on annule la navigation
    if (this.longPressTriggered) {
      this.longPressTriggered = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.openAppNav(app);
  }

  private toggleBookmarkFromTile(app: AccessApp): void {
    if (app.is_bookmarked) {
      this.deleteBookmark(app.id_application);
    } else {
      this.addBookmark(app.id_application);
    }
  }

  getPathByKey(key: string | null): string {
    switch (key) {
      case 'admin_user_account_right':
        return '/portail/admin/utilisateur';
      case 'admin_business_introducer_right':
        return '/portail/admin/apporteur-affaire';
      case 'admin_job_right':
        return '/portail/admin/job';
      case 'admin_affaire_right':
        return '/portail/admin/affaire';
      case 'admin_brand_right':
        return '/portail/admin/marque';
      case 'admin_plaque_right':
        return '/portail/admin/plaque';
      case 'admin_model_right':
        return '/portail/admin/vehicule';
      case 'admin_phone_book_right':
        return '/portail/admin/annuraire';
      case 'admin_user_tracking_right':
        return '/portail/admin/suivi-utilisateur';
      default:
        return '/';
    }
  }

  hasAccessToAdminGate(): boolean {
    return (
      this.store.user().is_admin_paddock ||
      this.store.user().admin_affaire_right !== 'not-allowed' ||
      this.store.user().admin_brand_right !== 'not-allowed' ||
      this.store.user().admin_business_introducer_right !== 'not-allowed' ||
      this.store.user().admin_job_right !== 'not-allowed' ||
      this.store.user().admin_model_right !== 'not-allowed' ||
      this.store.user().admin_plaque_right !== 'not-allowed' ||
      this.store.user().admin_phone_book_right !== 'not-allowed' ||
      this.store.user().admin_user_account_right !== 'not-allowed' ||
      this.store.user().admin_user_tracking_right !== 'not-allowed'
    );
  }

  getUserAccessAdminGate(user: UserData): AccessAdminGate {
    return Object.fromEntries(Object.entries(user).filter(([key]) => key.endsWith('_right'))) as AccessAdminGate;
  }

  toggleCollapseNav() {
    this.navbarState.navCollapse.set(!this.navbarState.navCollapse());
    this.closeSideDrawer();
  }

  getStatusTooltip(): string {
    return `API: ${this.apiStatusRes.error() ? 'K.O' : 'O.K'} - WebSocket: ${this.wsStatusRes.error() ? 'K.O' : 'O.K'}`;
  }
  shouldNotify(key: string) {
    const k = `alert_${key}_last`;
    const last = Number(localStorage.getItem(k) ?? 0);
    const now = Date.now();
    if (now - last < this.COOLDOWN_MS) return false;
    localStorage.setItem(k, String(now));
    return true;
  }

  private sendDiscord(content: string) {
    if (!environment.enableDiscordAlert) return;
    return this.http
      .post('https://discord.com/api/webhooks/1473670716074229813/K0-VLTKaKjiYp9bYXRU7aUSpUZqvIow6as_PR4tHDJ6ty-sFw3-GNCirjynVjhYj2Qhj', { content })
      .subscribe();
  }
}
